import qs from 'qs'

import { fetchExportFile } from '@features/expertReports'
import type { ExpertReportCompareWith, ReportExportFormat } from '@features/expertReports'

import { getCurrentLocale, getRequest } from '../../utils'
import type { ExpertProfitAndLossReport } from './types/expertProfitAndLossReport'

export const EXPERT_PROFIT_AND_LOSS_COMPARE_COUNT = 1

export interface ExpertProfitAndLossQueryParams {
  /** Encoded period string, e.g. `fiscalyear:<organizationId>,2026`. */
  period: string
  compareCount: number
  compareWith: ExpertReportCompareWith
  /** Line label substring filter. Omitted from the request when empty or whitespace-only. */
  search?: string
}

export interface FetchExpertProfitAndLossParams extends ExpertProfitAndLossQueryParams {
  organizationId: string
}

export interface FetchExpertProfitAndLossExportParams extends ExpertProfitAndLossQueryParams {
  format: ReportExportFormat
  organizationId: string
}

const buildExpertProfitAndLossQueryString = ({
  compareCount,
  compareWith,
  period,
  search,
}: ExpertProfitAndLossQueryParams): string => {
  const trimmedSearch = search?.trim()

  return qs.stringify({
    compareCount,
    compareWith,
    period,
    ...(trimmedSearch ? { search: trimmedSearch } : {}),
  })
}

const getExpertProfitAndLossExportFallbackFileName = (format: ReportExportFormat): string => `Profit-and-loss.${format}`

export const fetchExpertProfitAndLoss = async ({
  compareCount,
  compareWith,
  organizationId,
  period,
  search,
}: FetchExpertProfitAndLossParams): Promise<ExpertProfitAndLossReport> => {
  const queryString = buildExpertProfitAndLossQueryString({ compareCount, compareWith, period, search })
  const { data } = await getRequest(`/v2/organizations/${organizationId}/reports/profitAndLoss?${queryString}`, {
    'accept-language': getCurrentLocale(),
  })

  return data
}

export const fetchExpertProfitAndLossExport = async ({
  compareCount,
  compareWith,
  format,
  organizationId,
  period,
  search,
}: FetchExpertProfitAndLossExportParams) => {
  const queryString = buildExpertProfitAndLossQueryString({ compareCount, compareWith, period, search })
  const path = `/v2/organizations/${organizationId}/reports/profitAndLoss.${format}?${queryString}`

  return fetchExportFile(path, format, getExpertProfitAndLossExportFallbackFileName(format))
}
